// Lib
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Util
import { manuallyReportError } from '../../../analytics/rollbarService';

// Selectors
import { getCurrentUser } from '../../../user/currentUserSelector';
import { getCapacitorPlatformSelector } from '../../../platform/platformSelector';

// Actions
import { fullPageNavigation, refreshPageAndClearLocalCache } from '../../../reducers/navigationActions';
import { intercomShowMessages } from '../../../analytics/analyticsActions';

// Components
import ModalActionsList from '../../modal/ModalActionsList';
import LinkButton from '../../buttons/LinkButton';

// Constants
import { getRootBoardId } from '../../../../common/users/userHelper';

type Props = {
    error: object;
};

/**
 * This component is intended to be used when a socket action fails after retrying many times.
 */
const SocketActionLimitExceededErrorContent: React.FC<Props> = ({ error }) => {
    const currentUser = useSelector(getCurrentUser);

    const capacitorPlatform = useSelector(getCapacitorPlatformSelector);
    const isCapacitorApp = capacitorPlatform !== 'web';

    const dispatch = useDispatch();

    useEffect(() => {
        manuallyReportError({
            error: error,
            sensitive: false,
        });
    }, []);

    const actions = [
        {
            text: 'Try again',
            func: (event: Event) => {
                event.preventDefault();
                dispatch(refreshPageAndClearLocalCache());
            },
        },
    ];

    const rootBoardId = getRootBoardId(currentUser);

    if (rootBoardId) {
        const loadRootBoard = (event: Event) => {
            const rootBoardId = getRootBoardId(currentUser);
            event.preventDefault();

            // Add # to beginning of URL for capacitor app,
            const url = isCapacitorApp ? `#/${rootBoardId}` : `/${rootBoardId}`;
            fullPageNavigation(url);
        };

        actions.push({ text: 'Load home board', func: loadRootBoard });
    }

    return (
        <>
            <h2>Milanote connection lost</h2>
            <div>
                <p>Milanote has lost connection with the server and must be reloaded to continue working.</p>
                <ModalActionsList actions={actions} />
            </div>
            <div className="section">
                <p>
                    Need help? <LinkButton onClick={() => dispatch(intercomShowMessages())}>Contact support</LinkButton>
                </p>
            </div>
        </>
    );
};

export default SocketActionLimitExceededErrorContent;
